import TurndownService from 'turndown';
import { safeHighlightSearch } from './security-utils';

export interface TOCItem {
  level: 2 | 3;
  text: string;
  anchor: string;
}

/**
 * Convert HTML to Markdown using Turndown
 */
export function htmlToMarkdown(html: string): string {
  if (!html || !html.trim().startsWith('<')) {
    return html;
  }
  
  const turndown = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
  });
  
  return turndown.turndown(html);
}

/**
 * Generate anchor ID from text
 */
export function generateAnchor(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Parse markdown and extract Table of Contents
 */
export function parseMarkdownTOC(markdown: string): TOCItem[] {
  const toc: TOCItem[] = [];
  const lines = markdown.split('\n');
  
  for (const line of lines) {
    const h2Match = line.match(/^##\s+(.+)$/);
    const h3Match = line.match(/^###\s+(.+)$/);
    
    if (h2Match) {
      const text = h2Match[1].trim();
      toc.push({
        level: 2,
        text,
        anchor: generateAnchor(text),
      });
    } else if (h3Match) {
      const text = h3Match[1].trim();
      toc.push({
        level: 3,
        text,
        anchor: generateAnchor(text),
      });
    }
  }
  
  return toc;
}

/**
 * Highlight search terms in text
 */
export function highlightSearchTerms(text: string, query: string): string {
  return safeHighlightSearch(text, query, '');
}

/**
 * Search within markdown content
 */
export function searchInMarkdown(
  markdown: string,
  query: string
): boolean {
  if (!query || query.trim() === '') return true;
  
  const normalizedQuery = query.toLowerCase();
  const normalizedContent = markdown.toLowerCase();
  
  return normalizedContent.includes(normalizedQuery);
}
